import { createSlice } from "@reduxjs/toolkit";
import moment from "moment";

const initialState = {
  selectedProperty: [],
  selectedTab: "Portfolio",
  sidebarOpen: false,
  properties: [],
  dateRange: [
    moment().startOf("month").format("YYYY-MM-DD"),
    moment().format("YYYY-MM-DD"),
  ], // Store date range for API calls
  propertyData: {},
  dashboardStats: {},
  expenseBreakdown: {},
  financialPerformanceGoal: {},
  financialMetrics: {
    "Ski Mtn Lodge": [
      {
        metric: "RevPAR",
        actual: "$198",
        target: "$180",
        brandAvg: "$165",
        compSet: "$170",
        trend: "+6.2%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$245",
        target: "$220",
        brandAvg: "$200",
        compSet: "$210",
        trend: "+4.8%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$125",
        target: "$110",
        brandAvg: "$95",
        compSet: "$100",
        trend: "+5.1%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "41.2%",
        target: "38.0%",
        brandAvg: "35.5%",
        compSet: "36.8%",
        trend: "+0.8%",
        status: "below",
      },
    ],
    "Maple Leaf Inn": [
      {
        metric: "RevPAR",
        actual: "$285",
        target: "$260",
        brandAvg: "$240",
        compSet: "$250",
        trend: "+8.1%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$320",
        target: "$290",
        brandAvg: "$270",
        compSet: "$280",
        trend: "+6.5%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$168",
        target: "$145",
        brandAvg: "$130",
        compSet: "$135",
        trend: "+7.2%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "45.8%",
        target: "42.0%",
        brandAvg: "39.2%",
        compSet: "40.5%",
        trend: "+2.1%",
        status: "below",
      },
    ],
    "Pikes Peak Inn": [
      {
        metric: "RevPAR",
        actual: "$225",
        target: "$210",
        brandAvg: "$190",
        compSet: "$195",
        trend: "+5.5%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$275",
        target: "$255",
        brandAvg: "$235",
        compSet: "$245",
        trend: "+4.2%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$138",
        target: "$125",
        brandAvg: "$112",
        compSet: "$118",
        trend: "+5.8%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "42.5%",
        target: "39.5%",
        brandAvg: "36.8%",
        compSet: "38.1%",
        trend: "+1.5%",
        status: "below",
      },
    ],
    "Lake Ridge Inn": [
      {
        metric: "RevPAR",
        actual: "$251",
        target: "$200",
        brandAvg: "$179",
        compSet: "$185",
        trend: "+7.5%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$295",
        target: "$250",
        brandAvg: "$231",
        compSet: "$240",
        trend: "+5.2%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$145",
        target: "$120",
        brandAvg: "$105",
        compSet: "$110",
        trend: "+4.8%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "43.5%",
        target: "40.0%",
        brandAvg: "37.1%",
        compSet: "38.5%",
        trend: "+1.2%",
        status: "below",
      },
    ],
    "Brentwood Inn": [
      {
        metric: "RevPAR",
        actual: "$312",
        target: "$280",
        brandAvg: "$260",
        compSet: "$270",
        trend: "+9.2%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$355",
        target: "$320",
        brandAvg: "$300",
        compSet: "$310",
        trend: "+7.8%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$192",
        target: "$165",
        brandAvg: "$150",
        compSet: "$155",
        trend: "+8.5%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "47.2%",
        target: "44.0%",
        brandAvg: "41.5%",
        compSet: "42.8%",
        trend: "+2.8%",
        status: "below",
      },
    ],
    "Uncle Tom's Inn": [
      {
        metric: "RevPAR",
        actual: "$175",
        target: "$165",
        brandAvg: "$150",
        compSet: "$155",
        trend: "+4.5%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$215",
        target: "$200",
        brandAvg: "$185",
        compSet: "$190",
        trend: "+3.8%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$108",
        target: "$100",
        brandAvg: "$88",
        compSet: "$92",
        trend: "+4.2%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "38.9%",
        target: "36.5%",
        brandAvg: "34.2%",
        compSet: "35.5%",
        trend: "+0.5%",
        status: "below",
      },
    ],
    "Portfolio Overview": [
      {
        metric: "RevPAR",
        actual: "$251",
        target: "$200",
        brandAvg: "$179",
        compSet: "$185",
        trend: "+7.5%",
        status: "exceeds",
      },
      {
        metric: "ADR",
        actual: "$295",
        target: "$250",
        brandAvg: "$231",
        compSet: "$240",
        trend: "+5.2%",
        status: "exceeds",
      },
      {
        metric: "GOPPAR",
        actual: "$145",
        target: "$120",
        brandAvg: "$105",
        compSet: "$110",
        trend: "+4.8%",
        status: "exceeds",
      },
      {
        metric: "Profit Margin",
        actual: "43.5%",
        target: "40.0%",
        brandAvg: "37.1%",
        compSet: "38.5%",
        trend: "+1.2%",
        status: "below",
      },
    ],
  },
  reveOccuTrends: {},
  forecastData: {},
  employeData: {},
  employeNameData: {},
  reviewData: {},
  otaReviewData: {},
  competitivePricingData: {},
  channelPerformanceData: {},
  annualExpenseBreakdown: {},
  annualEmployeData: {},
  sustainabilityData: {},
  sustainabilityRoomNightData: {},
};

const investorDashboardSlice = createSlice({
  name: "investorDashboard",
  initialState: initialState,
  reducers: {
    setSelectedProperty: (state, action) => {
      state.selectedProperty = action.payload;
    },
    setSelectedTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    setPropertyData: (state, action) => {
      const { propertyName, data } = action.payload;
      state.propertyData[propertyName] = {
        ...state.propertyData[propertyName],
        ...data,
      };
    },
    setFinancialMetrics: (state, action) => {
      const { propertyName, metrics } = action.payload;
      state.financialMetrics[propertyName] = metrics;
    },
    updatePropertyData: (state, action) => {
      const { propertyName, key, value } = action.payload;
      if (!state.propertyData[propertyName]) {
        state.propertyData[propertyName] = {};
      }
      state.propertyData[propertyName][key] = value;
    },
    setProperties: (state, action) => {
      state.properties = action.payload;
    },
    setPropertiesFailed: (state) => {
      state.properties = [];
    },
    setDashboardStats: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.dashboardStats[key] = {
        ...stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    clearDashboardStats: (state) => {
      state.dashboardStats = null;
    },
    setReveOccuTrends: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.reveOccuTrends[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    clearReveOccuTrends: (state) => {
      state.reveOccuTrends = null;
    },
    setExpenseBreakdown: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.expenseBreakdown[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    clearExpenseBreakdown: (state) => {
      state.expenseBreakdown = null;
    },
    setFinancialPerformanceGoal: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.financialPerformanceGoal[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    setForecastData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.forecastData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    setEmployeData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.employeData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    setEmployeNameData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      // Store stats with cache key (hotel_id + date_range) to prevent redundant API calls
      const key = cacheKey || propertyName;
      state.employeNameData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(), // Track when data was fetched
      };
    },
    setReviewData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.reviewData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setOtaReviewData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.otaReviewData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setCompetitivePricingData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.competitivePricingData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setChannelPerformanceData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.channelPerformanceData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setAnnualExpenseBreakdown: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.annualExpenseBreakdown[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setAnnualEmployeData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.annualEmployeData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setSustainabilityData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.sustainabilityData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    setSustainabilityRoomNightData: (state, action) => {
      const { propertyName, stats, cacheKey } = action.payload;
      const key = cacheKey || propertyName;
      state.sustainabilityRoomNightData[key] = {
        data: stats,
        _cacheKey: key,
        _timestamp: Date.now(),
      };
    },
    clearFinancialPerformanceGoal: (state) => {
      state.financialPerformanceGoal = null;
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
  },
});

export const {
  setSelectedProperty,
  setSelectedTab,
  setSidebarOpen,
  setPropertyData,
  setFinancialMetrics,
  updatePropertyData,
  setProperties,
  setPropertiesFailed,
  setDashboardStats,
  clearDashboardStats,
  setDateRange,
  setReveOccuTrends,
  clearReveOccuTrends,
  setExpenseBreakdown,
  clearExpenseBreakdown,
  setFinancialPerformanceGoal,
  setForecastData,
  setEmployeData,
  setEmployeNameData,
  setReviewData,
  setOtaReviewData,
  setCompetitivePricingData,
  setChannelPerformanceData,
  setAnnualExpenseBreakdown,
  setAnnualEmployeData,
  setSustainabilityData,
  setSustainabilityRoomNightData,
  clearFinancialPerformanceGoal,
} = investorDashboardSlice.actions;

// Selectors for getting property-specific data
export const selectSelectedProperty = (state) =>
  state.investorDashboard.selectedProperty;
export const selectSelectedTab = (state) => state.investorDashboard.selectedTab;
export const selectProperties = (state) => state.investorDashboard.properties;
export const selectFinancialMetrics = (state, propertyName) =>
  state.investorDashboard.financialMetrics[propertyName] || [];
export const selectPropertyData = (state, propertyName) =>
  state.investorDashboard.propertyData[propertyName] || {};
export const selectDashboardStats = (state, propertyName) =>
  state.investorDashboard.dashboardStats[propertyName] || null;
export const selectDateRange = (state) => state.investorDashboard.dateRange;
export const selectReveOccuTrends = (state, propertyName) =>
  state.investorDashboard.reveOccuTrends[propertyName] || null;
export const selectExpenseBreakdown = (state, propertyName) =>
  state.investorDashboard.expenseBreakdown[propertyName] || null;
export const selectFinancialPerformanceGoal = (state, propertyName) =>
  state.investorDashboard.financialPerformanceGoal[propertyName] || null;
export const selectForecastData = (state, propertyName) =>
  state.investorDashboard.forecastData[propertyName] || null;
export const selectEmployeData = (state, propertyName) =>
  state.investorDashboard.employeData[propertyName] || null;
export const selectEmployeNameData = (state, propertyName) =>
  state.investorDashboard.employeNameData[propertyName] || null;
export const selectReviewData = (state, propertyName) =>
  state.investorDashboard.reviewData[propertyName] || null;
export const selectOtaReviewData = (state, propertyName) =>
  state.investorDashboard.otaReviewData[propertyName] || null;
export const selectCompetitivePricingData = (state, propertyName) =>
  state.investorDashboard.competitivePricingData[propertyName] || null;
export const selectChannelPerformanceData = (state, propertyName) =>
  state.investorDashboard.channelPerformanceData[propertyName] || null;
export const selectAnnualExpenseBreakdown = (state, propertyName) =>
  state.investorDashboard.annualExpenseBreakdown[propertyName] || null;
export const selectAnnualEmployeData = (state, propertyName) =>
  state.investorDashboard.annualEmployeData[propertyName] || null;
export const selectSustainabilityData = (state, propertyName) =>
  state.investorDashboard.sustainabilityData[propertyName] || null;
export const selectSustainabilityRoomNightData = (state, propertyName) =>
  state.investorDashboard.sustainabilityRoomNightData[propertyName] || null;

export default investorDashboardSlice.reducer;
